import { Option } from 'models';
import React, { useState } from 'react';

import { SasIcon } from '../../SasIcon';
import { GenericFieldProps } from '../util/types';

import * as styles from './styles';

type SelectInputProps = {
  showEmptyOption?: boolean;
  options: Option[];
} & GenericFieldProps &
  JSX.IntrinsicElements['select'];

export const SelectField = function ({
  name,
  label,
  options,
  error,
  description,
  className,
  id,
  required,
  onFocus,
  onBlur,
  value,
  ...props
}: SelectInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const prefix = React.useId();
  const inputId = id ?? `${prefix}-${name}`;

  const hasError = Boolean(error) && !isFocused;
  const hasDescription = Boolean(description);
  const raised = isFocused || Boolean(value);

  const optionComponents = options.map((o: Option, index: number) => {
    return (
      <option key={index} value={o.value}>
        {o.label}
      </option>
    );
  });

  const handleFocus = (e: React.FocusEvent<HTMLSelectElement>) => {
    setIsFocused(true);
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
    setIsFocused(false);
    if (onBlur) onBlur(e);
  };

  return (
    <div css={styles.inputWrapper}>
      <div css={styles.fieldStyles}>
        <select
          {...props}
          id={inputId}
          name={name}
          value={value}
          css={styles.inputStyles(hasError, isFocused)}
          data-testid={`${name} select`}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={className}
        >
          <option disabled hidden value=""></option>
          {optionComponents}
        </select>

        <label htmlFor={inputId} css={styles.fieldLabel(raised)}>
          {label}
          {required && <sup>*</sup>}
        </label>

        <div css={styles.selectArrow}>
          <SasIcon name="in--arrow-down" />
        </div>
      </div>

      <div css={styles.fieldInfo}>
        {hasError && <p css={styles.error}>{error}</p>}
        {hasDescription && !hasError && <p css={styles.description}>{description}</p>}
      </div>
    </div>
  );
};
